import { css } from '@compiled/react';

import { Button } from '@/libraries/design/components/button/src/button';

import { useGoToNextStep, useGoToPrevStep, useSetStep } from '../context/hooks';

import type { Step } from '../context/types';

export interface StepNavProps {
	showBack?: boolean;
	showNext?: boolean;
	showSkip?: boolean;
	nextDisabled?: boolean;
	skipTo?: Step;
	nextLabel?: string;
}

/**
 * Bottom navigation row for each calculator step. Renders WAC design-system
 * `Button`s so the calculator inherits the shared button styling, focus rings,
 * and accessibility behaviour.
 *
 * Layout notes:
 *  - The row is centered horizontally within the calculator container so the
 *    primary "Next" action sits in the middle of the page, per Figma.
 *  - 76px top padding separates the nav from the last question row (the
 *    "How many people are in your organization?" row).
 */
export const StepNav = (props: StepNavProps) => {
	const {
		showBack = false,
		showNext = false,
		showSkip = false,
		nextDisabled = false,
		skipTo,
		nextLabel = 'Next',
	} = props;
	const prev = useGoToPrevStep();
	const next = useGoToNextStep();
	const setStep = useSetStep();

	return (
		<div css={navRowStyle}>
			{showBack && (
				<Button variant="secondary" onClick={prev}>
					Back
				</Button>
			)}
			{showSkip && skipTo && (
				<Button variant="secondary" onClick={() => setStep(skipTo)}>
					Skip
				</Button>
			)}
			{showNext && (
				<Button variant="primary" isDisabled={nextDisabled} onClick={next}>
					{nextLabel}
				</Button>
			)}
		</div>
	);
};

// Centered nav row with 76px top padding per Figma. Buttons flow horizontally
// with a small gap between them.
const navRowStyle = css({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	gap: '12px',
	paddingBlockStart: '76px',
	width: '100%',
});
